/**
 * voucher-code-entry — the panel a buyer types a code into, and reads the
 * answer in. Mounted by `show()` from the page's own code run
 * (voucher-code.view.ts).
 *
 * Why the code lives in an overlay at all: most buyers hold none, so a
 * permanently open field spends the highest-stakes strip of the page — the one
 * directly above Pay — on a minority path. And the round trip is seconds long,
 * so resolving inside an overlay means the answer, whichever of the five it
 * is, cannot reflow the page underneath it. The page keeps one fixed run;
 * everything that grows, shrinks or changes its mind happens in here.
 *
 * Nothing moves in here either. The outcome region is a single grid cell with
 * EVERY arm rendered into it — idle, pending, applied, refused, dropped — and
 * all but the active one `invisible`, which keeps its box, drops it from the
 * tab order, and with `aria-hidden` from the accessibility tree. The cell is
 * therefore as tall as the tallest thing it can ever say, from first paint, in
 * every language, with no measured pixel number anywhere. A `when()`/`choose()`
 * dispatch here would render one arm and resize the sheet under the buyer's
 * finger every time the answer changed — which is exactly the transition this
 * surface exists to perform.
 *
 * That is also why the idle arm carries the general rules about codes rather
 * than an empty box: the reserve has to be filled by something, and the most
 * useful thing to put in it is what the refusal will later say — read BEFORE
 * typing, where it sets expectations instead of consoling.
 *
 * The refusal: every cause renders one identical answer, because a refusal
 * that distinguishes its causes is an oracle over which codes exist, when they
 * expire and how many redemptions each has left. What keeps it honest rather
 * than a shrug — the typed code stays in the field, focused and selected,
 * since most refusals a real buyer meets are their own typo and handing back
 * what they typed is the one diagnostic that discloses nothing; the body
 * states what is true of codes in GENERAL, the same sentence the idle arm
 * already showed, so it reads as the rule rather than a hint about which rule
 * fired; and it is not styled as a failure, because a payment refusal on this
 * same journey owns the error red and a code that does not apply must never be
 * mistaken for a declined card.
 *
 * Applied never promises money off. A code that only reveals hidden types, and
 * one whose discount finds nothing eligible yet, both apply at
 * `discountCents: 0` — so "applied" says the code was accepted, and what it
 * did is whatever the basket now shows.
 *
 * The applied arm is also the ONLY place a buyer learns a ticket type became
 * available to them. A `claimableBy: "code"` type is hidden from the shop
 * until the code reveals it, and the reveal lands while this overlay covers
 * the page — so the arm names the revealed types and offers the way to them,
 * or the row appears behind the scrim and is never noticed.
 */

import { formatCurrency } from "@mhmo91/rx";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import type { SchmancyInput } from "@mhmo91/schmancy/form";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type PropertyValues, type TemplateResult } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { basketGross, shopTypeRows } from "../event-page/event-page.state.ts";
import {
	VOUCHER_ATTEMPT_CAP,
	type VoucherPhase,
	voucherCleared$,
	voucherDraft,
	voucherEntry,
	voucherSubmitted$,
} from "./code.state.ts";

/** Every arm the outcome cell can ever show. The order is the reading order of
 *  the DOM, never a priority: exactly one is visible at a time and the cell's
 *  height is the maximum over all of them. */
const ARMS: readonly VoucherPhase[] = [
	"idle",
	"pending",
	"applied",
	"refused",
	"dropped",
];

/** Refusals after which the buyer is told, once, that nobody on this page can
 *  see why. The client counting its OWN submissions discloses nothing the
 *  buyer did not already do — and it REPLACES the rules paragraph rather than
 *  joining it, so the cell says something new without growing. */
const NUDGE_AFTER = 2;

@schmancyElement("voucher-code-entry")
export class VoucherCodeEntry extends LocalizeMixin(SchmancyElement) {
	private readonly field = createRef<SchmancyInput>();
	private lastPhase: VoucherPhase = "idle";

	override firstUpdated(changed: PropertyValues) {
		super.firstUpdated(changed);
		this.field.value?.focus();
	}

	/** A refusal is not the field's fault and the fix IS the field, so focus
	 *  goes back to it with the text selected — one keystroke from a corrected
	 *  code. (`<schmancy-alert>` is deliberately not used for this outcome: it
	 *  takes focus itself, which is right for a payment failure whose remedy
	 *  lives elsewhere and wrong for a typo.) */
	override updated(changed: PropertyValues) {
		super.updated(changed);
		const phase = voucherEntry.value.phase;
		if (phase === this.lastPhase) return;
		this.lastPhase = phase;
		if (phase !== "refused") return;
		this.field.value?.focus();
		this.field.value?.select();
	}

	override render(): TemplateResult {
		const entry = voucherEntry.value;
		const spent = entry.attempts >= VOUCHER_ATTEMPT_CAP;
		const applied = entry.phase === "applied";
		const unlockedNames = shopTypeRows
			.get()
			.filter((r) => entry.unlockedTypeIds.includes(r.id))
			.map((r) => r.name)
			.join(", ");

		// One sentence for every landed answer, computed once rather than
		// written into two arms.
		const appliedLine =
			unlockedNames === ""
				? this.t("storefront.voucher.applied")
				: this.t(
						entry.discountCents === 0
							? "storefront.voucher.appliedUnlockOnly"
							: "storefront.voucher.appliedUnlock",
						{ types: unlockedNames },
					);

		const arm = (phase: VoucherPhase): TemplateResult => {
			if (phase === "applied")
				return html`
					${when(
						entry.discountCents > 0,
						() => html`
							<div class="flex items-baseline gap-2">
								<schmancy-typography
									type="body"
									token="sm"
									class="text-surface-onVariant line-through"
								>
									${formatCurrency(basketGross.get() / 100, "EUR", this.locale)}
								</schmancy-typography>
								<schmancy-typography type="title" token="lg" class="text-success-default">
									${formatCurrency(
										(basketGross.get() - entry.discountCents) / 100,
										"EUR",
										this.locale,
									)}
								</schmancy-typography>
							</div>
							<schmancy-typography type="body" token="sm" class="text-success-default">
								${this.t("storefront.voucher.youSaved", {
									amount: formatCurrency(
										entry.discountCents / 100,
										"EUR",
										this.locale,
									),
								})}
							</schmancy-typography>
						`,
					)}
					<schmancy-typography type="body" token="sm">${appliedLine}</schmancy-typography>
					${when(
						entry.unlockedTypeIds.length > 0,
						() => html`
							<schmancy-button
								class="self-start"
								variant="text"
								size="sm"
								@click=${() => {
									// Both, in this order: the page needs the request before
									// the overlay that made it goes away.
									this.dispatchEvent(
										new CustomEvent("show-tickets", {
											bubbles: true,
											composed: true,
										}),
									);
									this.dispatchEvent(
										new CustomEvent("close", { bubbles: true, composed: true }),
									);
								}}
							>
								<schmancy-icon slot="prefix" aria-hidden="true">arrow_upward</schmancy-icon>
								${this.t("storefront.voucher.showTickets")}
							</schmancy-button>
						`,
					)}
				`;
			if (phase === "dropped")
				return html`
					<schmancy-typography type="body" token="sm">
						${this.t("storefront.voucher.dropped", { code: entry.code ?? "" })}
					</schmancy-typography>
				`;
			if (phase === "refused")
				return html`
					<schmancy-typography type="label" token="md">
						${this.t(
							spent
								? "storefront.voucher.spentHeadline"
								: "storefront.voucher.refusedHeadline",
						)}
					</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.t(
							spent
								? "storefront.voucher.spentBody"
								: entry.attempts > NUDGE_AFTER
									? "storefront.voucher.refusedAgain"
									: "storefront.voucher.refusedBody",
						)}
					</schmancy-typography>
				`;
			return html`
				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
					${this.t("storefront.voucher.rules")}
				</schmancy-typography>
			`;
		};

		return html`
			<div ${overflowWithin()} class="flex w-md max-w-full flex-col gap-4 p-5">
				<schmancy-input
					${ref(this.field)}
					label=${this.t("storefront.voucher.label")}
					placeholder=${this.t("storefront.voucher.placeholder")}
					name="voucher"
					autocomplete="off"
					spellcheck="false"
					?disabled=${spent && !applied}
					?readonly=${entry.phase === "pending" || applied}
					?error=${entry.phase === "refused"}
					.value=${applied ? (entry.code ?? "") : voucherDraft.value}
					@input=${(e: CustomEvent<{ value: string }>) => {
						const next = e.detail.value.toUpperCase();
						if (next === voucherDraft.value) return;
						voucherDraft.replace(next);
					}}
					@keydown=${(e: KeyboardEvent) => {
						if (e.key !== "Enter") return;
						e.preventDefault();
						if (voucherEntry.value.phase === "pending" || applied || spent)
							return;
						voucherSubmitted$.next(voucherDraft.value);
					}}
				>
					<!-- The action keeps its box through every state — disabled while
					     the round trip runs, relabelled once a code stands. Swapping it
					     for a spinner would change the trailing band's width, and with
					     it the text inset the buyer's own code sits at. -->
					<schmancy-button
						slot="actions"
						variant="text"
						size="sm"
						?disabled=${!applied && (spent || voucherDraft.value.trim() === "")}
						.busy=${entry.phase === "pending"}
						@click=${() => {
							if (voucherEntry.value.phase === "pending") return;
							if (applied) voucherCleared$.next();
							else voucherSubmitted$.next(voucherDraft.value);
						}}
					>
						${this.t(
							applied
								? "storefront.voucher.remove"
								: "storefront.voucher.apply",
						)}
					</schmancy-button>
				</schmancy-input>

				<!-- One cell, every arm, one visible. See this file's header. -->
				<div role="status" aria-live="polite" class="grid">
					${repeat(
						ARMS,
						(a) => a,
						(a) => html`
							<div
								class=${this.classMap({
									"col-start-1 row-start-1 flex flex-col gap-2": true,
									invisible: a !== entry.phase,
								})}
								aria-hidden=${a === entry.phase ? "false" : "true"}
							>
								${arm(a)}
							</div>
						`,
					)}
				</div>

				<schmancy-button
					variant="outlined"
					width="full"
					@click=${() =>
						this.dispatchEvent(
							new CustomEvent("close", { bubbles: true, composed: true }),
						)}
				>
					${this.t(
						applied ? "storefront.voucher.done" : "storefront.voucher.cancel",
					)}
				</schmancy-button>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"voucher-code-entry": VoucherCodeEntry;
	}
}
